/* eslint-disable no-case-declarations */
import React, {
  createContext, useReducer, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import useQuery from 'hooks/useQuery';
import useDebounce from 'hooks/useDebounce';
import mAccount from 'models/cpm/account';
import useMutation2 from 'hooks/useMutation2';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { useToast } from 'components/toast/Toast';
import { useIntl } from 'react-intl';
import useSearchParams from '../hooks/useSearchParams';
import { createFiltersReducer, createSearch, createSort } from '../utils/filters';

const AccountsContext = createContext();

const DEFAULT_FILTERS = {
  search: {
    type: 'string',
    values: null,
    operator: 'ilike',
    formatter: (x) => x,
    filtersApiNames: ['name', 'phone', 'email', 'invoiceNumber'],
    displayUrl: true,
  },
  carpark: {
    type: 'string',
    values: null,
    operator: 'ilike',
    formatter: (x) => x,
    filtersApiNames: ['carparkName'],
    displayUrl: true,
  },
  status: {
    type: 'string',
    values: null,
    fetchTrigger: null,
    sort: 'ASC',
    operator: 'eq',
    formatter: (x) => x,
    filtersApiNames: ['status'],
    displayUrl: true,
  },
};

function AccountsProvider({ children }) {
  const token = useSelector((state) => _get(state, 'auth.token', null));
  const { showToast } = useToast();
  const { formatMessage: t } = useIntl();
  const refetch = useRef(false);
  const refetchListAccounts = () => { refetch.current = !refetch.current; };

  /* 🔍 Filters */
  /* ---------- */

  const [filters, dispatchFilters] = useReducer(createFiltersReducer(DEFAULT_FILTERS, () => {}), DEFAULT_FILTERS);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);

  // 🌍 We first update filters with url search params at page init then filters update search params while using them
  useSearchParams({
    filters,
    dispatchFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  // We add a 🤏 debounce on the search input to avoid triggering too many requests
  // when the user is typing
  const DEBOUNCE_DELAY = 500;
  const searchDebounced = useDebounce(_get(filters, 'search.values[0]', null), DEBOUNCE_DELAY);
  const carparkId = _get(filters, 'carpark.values[0]', null);
  const variables = {
    input: {
      ...(carparkId ? { carparkId } : null),
    },
    search: createSearch(filters),
    sort: createSort(filters),
    pagination: {
      page_number: page,
      page_size: pageSize,
    },
  };

  const { data, /*  error, */ loading: accountsLoading } = useQuery({
    query: mAccount.queListAccounts,
    token,
    variables,
    dependencies: [
      // Automatic refetch
      _get(filters, 'status.fetchTrigger'),
      _get(filters, 'status.sort'),
      _get(filters, 'carpark.values[0]'),
      searchDebounced,
      // Paginations changes
      page,
      pageSize,
      refetch.current,
    ],
    // Fetch only if search params have been parsed from url
    // canFetch: searchParams,
  });

  /* 📦 Archive account */
  /* ------------------ */
  const [isModalArchiveOpen, setIsModalArchiveOpen] = React.useState(false);
  const accountId = useRef(null);

  const handleOpenModalArchive = (selectedAccountId) => {
    setIsModalArchiveOpen(true);
    accountId.current = selectedAccountId;
  };

  const handleCloseModalArchive = () => {
    setIsModalArchiveOpen(false);
    accountId.current = null;
  };

  const mutArchive = useMutation2({
    query: mAccount.mutArchiveAccount,
    token,
  });
  const archiveAccount = async () => {
    if (!accountId.current) {
      return;
    }
    await mutArchive.exec({ input: { id: accountId.current } });
  };

  // ✅ User archived !
  useEffect(() => {
    if (_get(mutArchive, 'data.archiveAccount.id', null)) {
      showToast({ message: t({ id: 'toast.successfulArchiveUser' }), type: 'success' });
      handleCloseModalArchive();
      refetchListAccounts();
    }
  }, [mutArchive.data]);
  // ❌ Error while archiving user
  useEffect(() => {
    const errorsObject = _get(mutArchive, 'errors', null);
    if (errorsObject) {
      Object.values(errorsObject).forEach((errorMessage) => {
        showToast({ id: 'archiveAccount', message: errorMessage, type: 'alert' });
        handleCloseModalArchive();
      });
    }
  }, [mutArchive.errors]);

  /* 🚮 Delete account */
  /* ----------------- */
  const [isModalDeleteAccountOpen, setIsModalDeleteAccountOpen] = React.useState(false);

  const handleOpenModalDeleteAccount = (selectedAccountId) => {
    setIsModalDeleteAccountOpen(true);
    accountId.current = selectedAccountId;
  };

  const handleCloseModalDeleteAccount = () => {
    setIsModalDeleteAccountOpen(false);
    accountId.current = null;
  };

  const mutDelete = useMutation2({
    query: mAccount.mutDeleteAccount,
    token,
  });
  const deleteAccount = async () => {
    if (!accountId.current) {
      return;
    }
    await mutDelete.exec({ input: { id: accountId.current } });
  };

  // ✅ User deleted !
  useEffect(() => {
    if (_get(mutDelete, 'data.deleteAccount', null)) {
      showToast({ message: t({ id: 'toast.successfulDeletedUser' }), type: 'success' });
      handleCloseModalDeleteAccount();
      refetchListAccounts();
    }
  }, [mutDelete.data]);
  // ❌ Error while deleting user
  useEffect(() => {
    const errorsObject = _get(mutDelete, 'errors', null);
    if (errorsObject) {
      Object.values(errorsObject).forEach((errorMessage) => {
        showToast({ id: 'deleteAccount', message: errorMessage, type: 'alert' });
        handleCloseModalDeleteAccount();
      });
    }
  }, [mutDelete.errors]);

  return (
    <AccountsContext.Provider
      value={{
        accounts: _get(data, 'listAccounts.entries', null),
        accountsLoading,

        pageNumber: _get(data, 'listAccounts.pageNumber', null),
        pageSize: _get(data, 'listAccounts.pageSize', null),
        totalEntries: _get(data, 'listAccounts.totalEntries', null),
        totalPages: _get(data, 'listAccounts.totalPages', null),

        filters,
        dispatchFilters,

        setPage,
        setPageSize,

        // 📦 Archive account
        isModalArchiveOpen,
        handleOpenModalArchive,
        handleCloseModalArchive,
        archiveAccount,
        isArchiving: mutArchive.loading,

        // 🚮 Delete account
        isModalDeleteAccountOpen,
        handleOpenModalDeleteAccount,
        handleCloseModalDeleteAccount,
        deleteAccount,
      }}
    >
      {children}
    </AccountsContext.Provider>
  );
}
AccountsProvider.propTypes = {
  children: PropTypes.node,
};
AccountsProvider.defaultProps = {
  children: null,
};

export { AccountsContext, AccountsProvider };



// WEBPACK FOOTER //
// ./routes/cpm/routes/accounts/contexts/AccountsContext.js